import React from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/mockData';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ArrowUpIcon, ArrowDownIcon, ZapIcon, AlertTriangleIcon } from 'lucide-react';
const DashboardHome: React.FC = () => {
  const {
    readings,
    threshold
  } = useData();
  const {
    isAdmin
  } = useAuth();
  // Get the last 24 readings (representing last 24 hours)
  const recentReadings = readings.slice(-24);
  // Calculate stats
  const currentReading = recentReadings.length > 0 ? recentReadings[recentReadings.length - 1].value : 0;
  const previousReading = recentReadings.length > 1 ? recentReadings[recentReadings.length - 2].value : 0;
  const percentChange = previousReading ? (currentReading - previousReading) / previousReading * 100 : 0;
  // Calculate average
  const averageReading = recentReadings.reduce((sum, reading) => sum + reading.value, 0) / recentReadings.length;
  // Calculate min and max
  const minReading = Math.min(...recentReadings.map(reading => reading.value));
  const maxReading = Math.max(...recentReadings.map(reading => reading.value));
  // Format data for chart
  const chartData = recentReadings.map(reading => ({
    time: new Date(reading.timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    }),
    value: reading.value
  }));
  return <div className="space-y-6" data-id="element-51">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-52">
        {/* Current Reading */}
        <div className="bg-white rounded-lg shadow p-4" data-id="element-53">
          <div className="flex justify-between items-start" data-id="element-54">
            <div data-id="element-55">
              <p className="text-gray-500 text-sm" data-id="element-56">Current Reading</p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-57">
                {currentReading.toFixed(2)} kWh
              </h3>
            </div>
            <div className={`p-2 rounded-full ${currentReading < threshold ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`} data-id="element-58">
              <ZapIcon className="h-5 w-5" data-id="element-59" />
            </div>
          </div>
          <div className="flex items-center mt-2" data-id="element-60">
            {percentChange > 0 ? <ArrowUpIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-61" /> : <ArrowDownIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-62" />}
            <span className={percentChange > 0 ? 'text-red-500' : 'text-green-500'} data-id="element-63">
              {Math.abs(percentChange).toFixed(1)}% from previous
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2" data-id="element-64">
            Last updated:{' '}
            {recentReadings.length > 0 ? formatDate(recentReadings[recentReadings.length - 1].timestamp) : 'N/A'}
          </p>
        </div>
        {/* Average Reading */}
        <div className="bg-white rounded-lg shadow p-4" data-id="element-65">
          <div className="flex justify-between items-start" data-id="element-66">
            <div data-id="element-67">
              <p className="text-gray-500 text-sm" data-id="element-68">24h Average</p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-69">
                {averageReading.toFixed(2)} kWh
              </h3>
            </div>
            <div className="p-2 rounded-full bg-blue-100 text-blue-600" data-id="element-70">
              <BarChart2Icon className="h-5 w-5" data-id="element-71" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2" data-id="element-72">Based on last 24 hours</p>
        </div>
        {/* Min Reading */}
        <div className="bg-white rounded-lg shadow p-4" data-id="element-73">
          <div className="flex justify-between items-start" data-id="element-74">
            <div data-id="element-75">
              <p className="text-gray-500 text-sm" data-id="element-76">24h Minimum</p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-77">
                {minReading.toFixed(2)} kWh
              </h3>
            </div>
            <div className="p-2 rounded-full bg-green-100 text-green-600" data-id="element-78">
              <TrendingDownIcon className="h-5 w-5" data-id="element-79" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2" data-id="element-80">
            {minReading < threshold && <span className="flex items-center text-red-500" data-id="element-81">
                <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-82" />
                Below threshold ({threshold} kWh)
              </span>}
            {minReading >= threshold && <span data-id="element-83">Above threshold ({threshold} kWh)</span>}
          </p>
        </div>
        {/* Max Reading */}
        <div className="bg-white rounded-lg shadow p-4" data-id="element-84">
          <div className="flex justify-between items-start" data-id="element-85">
            <div data-id="element-86">
              <p className="text-gray-500 text-sm" data-id="element-87">24h Maximum</p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-88">
                {maxReading.toFixed(2)} kWh
              </h3>
            </div>
            <div className="p-2 rounded-full bg-purple-100 text-purple-600" data-id="element-89">
              <TrendingUpIcon className="h-5 w-5" data-id="element-90" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2" data-id="element-91">Peak consumption</p>
        </div>
      </div>
      {/* Energy Chart */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-92">
        <h3 className="text-lg font-semibold mb-4" data-id="element-93">
          Energy Consumption (Last 24 Hours)
        </h3>
        <div className="h-80" data-id="element-94">
          <ResponsiveContainer width="100%" height="100%" data-id="element-95">
            <LineChart data={chartData} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-96">
              <CartesianGrid strokeDasharray="3 3" data-id="element-97" />
              <XAxis dataKey="time" data-id="element-98" />
              <YAxis domain={['auto', 'auto']} data-id="element-99" />
              <Tooltip data-id="element-100" />
              <ReferenceLine y={threshold} stroke="red" strokeDasharray="3 3" label={{
              value: `Threshold (${threshold} kWh)`,
              position: 'insideTopRight',
              fill: 'red'
            }} data-id="element-101" />
              <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{
              r: 3
            }} activeDot={{
              r: 5
            }} data-id="element-102" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-103">
        <h3 className="text-lg font-semibold mb-4" data-id="element-104">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-105">
          <button onClick={() => window.location.href = '/dashboard/notifications'} className="flex items-center justify-center p-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors" data-id="element-106">
            <MessageSquareIcon className="h-5 w-5 mr-2" data-id="element-107" />
            <span data-id="element-108">Send Notifications</span>
          </button>
          <button onClick={() => window.location.href = '/dashboard/history'} className="flex items-center justify-center p-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors" data-id="element-109">
            <ClockIcon className="h-5 w-5 mr-2" data-id="element-110" />
            <span data-id="element-111">View Historical Data</span>
          </button>
          <button onClick={() => window.location.href = '/dashboard/export'} className="flex items-center justify-center p-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors" data-id="element-112">
            <DownloadIcon className="h-5 w-5 mr-2" data-id="element-113" />
            <span data-id="element-114">Export Data</span>
          </button>
        </div>
      </div>
      {isAdmin && <div className="bg-white rounded-lg shadow p-4" data-id="element-115">
          <h3 className="text-lg font-semibold mb-4" data-id="element-116">Admin Controls</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-117">
            <button onClick={() => window.location.href = '/dashboard/settings'} className="flex items-center justify-center p-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors" data-id="element-118">
              <SettingsIcon className="h-5 w-5 mr-2" data-id="element-119" />
              <span data-id="element-120">Threshold Settings</span>
            </button>
            <button onClick={() => window.location.href = '/dashboard/users'} className="flex items-center justify-center p-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors" data-id="element-121">
              <UsersIcon className="h-5 w-5 mr-2" data-id="element-122" />
              <span data-id="element-123">Manage Users</span>
            </button>
          </div>
        </div>}
    </div>;
};
// Import missing Lucide icons
import { BarChart2Icon, TrendingUpIcon, TrendingDownIcon, MessageSquareIcon, DownloadIcon, UsersIcon, SettingsIcon, ClockIcon } from 'lucide-react';
export default DashboardHome;